import { Asset, useRouter, useTags } from "@tanstack/react-router";
import { createElement } from "react";

import { SITE } from "@/lib/site";

type Tag = ReturnType<typeof useTags>[number];
type Attrs = Record<string, unknown>;

/**
 * Vervangt <HeadContent />: dezelfde route-tags, maar altijd in één vaste,
 * crawlervriendelijke volgorde en aangevuld met afgeleide tags
 * (twitter:*, og:site_name, hreflang, paginaspecifieke og:image:alt).
 */
const META_ORDER = [
  "charset", "viewport", "title", "description", "robots", "googlebot",
  "og:site_name", "og:locale", "og:type", "og:url", "og:title", "og:description",
  "og:image", "og:image:width", "og:image:height", "og:image:alt",
  "twitter:card", "twitter:title", "twitter:description", "twitter:image", "twitter:image:alt",
  "author", "article:published_time", "article:modified_time", "theme-color",
];
const LINK_ORDER = ["canonical", "alternate", "icon", "apple-touch-icon", "manifest", "preload", "stylesheet"];
const SECTION_LABELS: Record<string, string> = {
  betaalmethoden: "Betaalmethoden",
  auteurs: "Redactie",
  nieuws: "Nieuws",
  "lage-storting": "Lage stortingen",
};
const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

function key(t: Tag): string {
  const a = (t.attrs ?? {}) as Attrs;
  if (t.tag === "title") return "title";
  if (t.tag === "meta") {
    if ("charSet" in a) return "charset";
    return String(a["name"] ?? a["property"] ?? "");
  }
  if (t.tag === "link") return String(a["rel"] ?? "");
  return "";
}

function rank(t: Tag): number {
  if (t.tag === "title" || t.tag === "meta") {
    const i = META_ORDER.indexOf(key(t));
    return i === -1 ? 50 : i;
  }
  if (t.tag === "link") {
    const i = LINK_ORDER.indexOf(key(t));
    return 100 + (i === -1 ? 20 : i);
  }
  if (t.tag === "style") return 130;
  if (t.tag === "script") {
    const a = (t.attrs ?? {}) as Attrs;
    if (a["type"] !== "application/ld+json") return 300;
    const body = String(t.children ?? "");
    if (body.includes('"@graph"')) return 200;
    if (body.includes('"BreadcrumbList"')) return 201;
    if (body.includes('"FAQPage"')) return 203;
    return 202;
  }
  return 400;
}

function metaValue(tags: Tag[], k: string): string | undefined {
  const t = tags.find((x) => x.tag === "meta" && key(x) === k);
  return t ? String(((t.attrs ?? {}) as Attrs)["content"] ?? "") : undefined;
}

function normalize(input: Tag[]): Tag[] {
  let tags = [...input];
  const has = (k: string) => tags.some((t) => key(t) === k);
  const addMeta = (attr: "name" | "property", k: string, content?: string) => {
    if (content && !has(k)) tags.push({ tag: "meta", attrs: { [attr]: k, content } } as Tag);
  };

  // Robots: altijd expliciet, uitgebreid op indexeerbare pagina's.
  const robots = metaValue(tags, "robots");
  tags = tags.filter((t) => key(t) !== "robots");
  const noindex = robots?.includes("noindex");
  tags.push({ tag: "meta", attrs: { name: "robots", content: noindex ? "noindex, follow" : ROBOTS_INDEX } } as Tag);

  const title = tags.find((t) => t.tag === "title")?.children as string | undefined;
  const ogTitle = metaValue(tags, "og:title") ?? title;
  const ogDesc = metaValue(tags, "og:description") ?? metaValue(tags, "description");
  addMeta("property", "og:site_name", SITE.name);
  addMeta("property", "og:locale", "nl_NL");
  addMeta("name", "twitter:title", ogTitle);
  addMeta("name", "twitter:description", ogDesc);

  // Paginaspecifieke alt voor de share-afbeelding.
  const cleanTitle = ogTitle?.replace(/\s*[|–-]\s*Casino ?Betalingen(\.com)?\s*$/i, "").trim();
  const imgAlt = cleanTitle ? `${cleanTitle} – ${SITE.name}` : undefined;
  if (metaValue(tags, "og:image")) {
    tags = tags.filter((t) => key(t) !== "og:image:alt" && key(t) !== "twitter:image:alt");
    addMeta("property", "og:image:alt", imgAlt);
    addMeta("name", "twitter:image:alt", imgAlt);
  }

  // og:url en hreflang volgen altijd de canonical.
  const canonical = tags.find((t) => t.tag === "link" && key(t) === "canonical");
  if (canonical) {
    const href = String(((canonical.attrs ?? {}) as Attrs)["href"]);
    tags = tags.filter((t) => key(t) !== "og:url");
    tags.push({ tag: "meta", attrs: { property: "og:url", content: href } } as Tag);
    if (!tags.some((t) => key(t) === "alternate")) {
      tags.push({ tag: "link", attrs: { rel: "alternate", hrefLang: "nl-NL", href } } as Tag);
      tags.push({ tag: "link", attrs: { rel: "alternate", hrefLang: "x-default", href } } as Tag);
    }
  }

  // BreadcrumbList op elke subpagina, ook waar de route er zelf geen meegeeft.
  if (canonical) {
    const href = String(((canonical.attrs ?? {}) as Attrs)["href"]);
    const path = href.replace(SITE.url, "");
    const hasCrumbs = tags.some((t) => t.tag === "script" && String(t.children ?? "").includes('"BreadcrumbList"'));
    if (path !== "/" && !hasCrumbs) {
      const segs = path.split("/").filter(Boolean);
      const items = [{ name: "Home", item: `${SITE.url}/` }];
      segs.forEach((seg, i) => {
        const url = `${SITE.url}/${segs.slice(0, i + 1).join("/")}/`;
        const last = i === segs.length - 1;
        items.push({ name: last ? cleanTitle ?? seg : SECTION_LABELS[seg] ?? seg, item: url });
      });
      tags.push({
        tag: "script",
        attrs: { type: "application/ld+json" },
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: items.map((x, i) => ({ "@type": "ListItem", position: i + 1, name: x.name, item: x.item })),
        }),
      } as Tag);
    }
  }

  return tags
    .map((t, i) => ({ t, i }))
    .sort((a, b) => rank(a.t) - rank(b.t) || a.i - b.i)
    .map(({ t }) => t);
}

export function SeoHead() {
  const tags = normalize(useTags());
  const nonce = useRouter().options.ssr?.nonce;
  return (
    <>
      {tags.map((tag) =>
        createElement(Asset, { ...tag, key: `tsr-meta-${JSON.stringify(tag)}`, ...(nonce ? { nonce } : {}) } as Parameters<typeof Asset>[0] & { key: string }),
      )}
    </>
  );
}
